<template>
    <Head title="Unsubscribe" />
    <div class="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        <div
            class="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-gray-200 mb-8 max-w-[40rem] mx-auto py-10 px-5"
        >
            <div v-if="!alreadyUnsubscribed" class="text-center space-y-6">
                <div>
                    <h1 class="text-2xl font-bold text-gray-900">
                        Unsubscribe from My10x emails
                    </h1>
                    <p class="mt-2 text-gray-600 text-sm">
                        {{ maskedEmail }}
                    </p>
                </div>

                <p class="text-gray-600">
                    You will no longer receive marketing emails from My10x.
                    You will still receive important account-related emails
                    such as password resets and billing notifications.
                </p>

                <form @submit.prevent="submit">
                    <button
                        type="submit"
                        :disabled="form.processing"
                        class="w-full inline-flex justify-center items-center px-6 py-3 bg-gray-800 text-white font-semibold rounded-md hover:bg-gray-700 disabled:opacity-50 transition"
                    >
                        <span v-if="form.processing">Unsubscribing…</span>
                        <span v-else>Unsubscribe from all marketing emails</span>
                    </button>
                </form>

                <p class="text-xs text-gray-400">
                    Prefer to manage individual preferences?
                    <a
                        href="/user-settings/notification-preferences"
                        class="underline hover:text-gray-600"
                    >
                        Manage preferences
                    </a>
                </p>
            </div>

            <div v-else class="space-y-6">
                <div class="text-center space-y-3">
                    <div
                        class="mx-auto flex items-center justify-center w-14 h-14 rounded-full border-2 border-teal-400"
                    >
                        <svg
                            class="w-7 h-7 text-teal-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                    </div>
                    <p class="text-lg font-bold text-gray-900">
                        {{ maskedEmail }}
                    </p>
                    <p class="font-semibold text-gray-800">
                        You have successfully unsubscribed from all email
                        communications.
                    </p>
                </div>

                <div
                    v-if="!reasonSubmitted"
                    class="bg-gray-50 border border-gray-200 rounded-md p-5 space-y-4"
                >
                    <p class="text-sm text-gray-700">
                        If you have a moment, please tell us why you
                        unsubscribed:
                    </p>
                    <div class="space-y-2">
                        <label
                            v-for="option in reasonOptions"
                            :key="option"
                            class="flex items-center gap-3 cursor-pointer"
                        >
                            <input
                                type="radio"
                                :value="option"
                                v-model="selectedReason"
                                class="text-orange-500 focus:ring-orange-400"
                            />
                            <span class="text-sm text-gray-700">{{
                                option
                            }}</span>
                        </label>
                    </div>
                    <button
                        @click="submitReason"
                        :disabled="!selectedReason || reasonForm.processing"
                        class="w-full px-4 py-2 bg-gray-700 text-white text-sm font-medium rounded-md hover:bg-gray-600 disabled:opacity-40 transition"
                    >
                        Submit
                    </button>
                </div>

                <div v-else class="text-center text-sm text-gray-500">
                    Thanks for your feedback.
                </div>

                <div class="text-center space-y-2 pt-2">
                    <p class="text-sm text-gray-500">
                        Made a mistake? Don't worry, you can change your
                        preferences.
                    </p>
                    <a
                        href="/user-settings/notification-preferences"
                        class="block w-full px-4 py-3 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 text-center transition"
                    >
                        Manage preferences
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { Head, useForm } from '@inertiajs/vue3';

const props = defineProps({
    token: String,
    maskedEmail: String,
    alreadyUnsubscribed: Boolean,
});

const form = useForm({});

function submit() {
    form.post(`/unsubscribe/${props.token}`);
}

const reasonOptions = [
    "I'm no longer interested.",
    "I didn't sign up for your mailing list.",
    'I receive too many emails.',
    'Your emails are not relevant to me.',
    'Other',
];

const selectedReason = ref(null);
const reasonSubmitted = ref(false);
const reasonForm = useForm({ reason: null });

function submitReason() {
    reasonForm.reason = selectedReason.value;
    reasonForm.post(`/unsubscribe/${props.token}`, {
        preserveState: true,
        onSuccess: () => {
            reasonSubmitted.value = true;
        },
    });
}
</script>
